import { createServerFn } from "@tanstack/react-start";
import { createOpenAI } from "@ai-sdk/openai";
import { Output, streamText } from "ai";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { logAiUsage } from "@/lib/aiTokenLog.server";
import { businessKindFor, CAL_COLORS, cleanColorLabels } from "@/lib/retailCalendarData";

/**
 * Radar de Sara IA para el calendario: propone fechas locales según sector,
 * comunidad y ciudad. Revisa como mucho una vez al mes por contexto (o si cambia
 * el sector/ciudad) y deja las propuestas como «suggested» para que el
 * empresario decida. Solo el servidor (service role) escribe filas de Sara IA.
 */
const MODEL = "openai/gpt-6-astra";

const OutSchema = z.object({
  events: z.array(z.object({
    date_start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    date_end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    category: z.enum(["sales", "offer", "purchases", "legal"]),
    title_es: z.string().max(120),
    title_en: z.string().max(120),
    reason_es: z.string().max(400),
    reason_en: z.string().max(400),
    color: z.string().nullable().optional(),
  })).max(8),
});

/** Contexto de caché: una revisión por sector/comunidad/ciudad y mes revisado (`month` = YYYY-MM). */
export function calendarAiContext(sector: string, region: string, city: string, now = new Date(), month?: string): string {
  const base = `${sector || "-"}|${region || "-"}|${city || "-"}|${now.toISOString().slice(0, 7)}`;
  return month && month !== now.toISOString().slice(0, 7) ? `${base}>${month}` : base;
}

export const refreshCalendarSuggestions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ lang: z.enum(["es", "en"]), month: z.string().regex(/^\d{4}-\d{2}$/).optional(), request: z.string().trim().max(300).optional() }).parse(d))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const { data: profile } = await sb.from("profiles").select("retail_sector, comunidad_autonoma, city, assistant_name, assistant_id").eq("user_id", context.userId).maybeSingle();
    const { data: prefs } = await sb.from("user_preferences").select("calendar_color_labels" as any).eq("user_id", context.userId).maybeSingle();
    const labels = cleanColorLabels((prefs as any)?.calendar_color_labels);
    const labelKeys = Object.keys(labels);
    const rawAssistant = String((profile as any)?.assistant_name || (profile as any)?.assistant_id || "").trim();
    const assistantName = rawAssistant ? rawAssistant.charAt(0).toUpperCase() + rawAssistant.slice(1, 30) : "Sara IA";
    const colorRule = labelKeys.length
      ? `El empresario usa este código de colores propio: ${labelKeys.map((k) => `${k} = «${labels[k as keyof typeof labels]}»`).join("; ")}. En cada fecha pon en color la clave que mejor encaje con su código, o null si ninguna encaja.`
      : "color: null siempre (el empresario aún no tiene código de colores).";
    const sector = (profile as any)?.retail_sector || "";
    const region = (profile as any)?.comunidad_autonoma || "";
    const city = (profile as any)?.city || "";
    const nowMonth = new Date().toISOString().slice(0, 7);
    const month = data.month && data.month >= nowMonth && data.month <= new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 7) ? data.month : nowMonth;
    const request = data.request?.trim() || "";
    // Una petición concreta del empresario siempre se atiende (no usa la caché mensual).
    const ctx = request ? `${calendarAiContext(sector, region, city, new Date(), month)}#${Date.now()}` : calendarAiContext(sector, region, city, new Date(), month);

    if (!request) {
      const { count } = await sb.from("user_calendar_events").select("id", { count: "exact", head: true })
        .eq("user_id", context.userId).eq("source", "ai_suggestion").eq("ai_context", ctx);
      if ((count ?? 0) > 0) return { ok: true as const, added: 0, cached: true, sector, region, city };
    }
    // Fechas que ya tiene en el mes: Sara no las repite.
    const { data: have } = await sb.from("user_calendar_events").select("title, date_start").eq("user_id", context.userId)
      .gte("date_start", `${month}-01`).lt("date_start", new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 1)).toISOString().slice(0, 10)).neq("title", "-").limit(40);
    const haveLine = (have ?? []).length ? `Ya tiene en el calendario: ${(have ?? []).map((h: any) => `${h.date_start} ${String(h.title).slice(0, 60)}`).join("; ")}.` : "";

    const apiKey = process.env["LOVABLE_API_KEY"];
    if (!apiKey) return { ok: false as const, error: "missing_key" };

    const today = new Date().toISOString().slice(0, 10);
    // Subvenciones que le interesan: Sara puede proponer cuándo prepararlas (sus fechas ya están en el calendario).
    let grantLine = "Sin subvenciones marcadas.";
    try {
      const { data: marks } = await sb.from("subvencion_marcas").select("codigo").eq("user_id", context.userId).eq("estado", "interesante").limit(20);
      const codes = (marks ?? []).map((m: any) => m.codigo);
      if (codes.length) {
        const { data: gs } = await sb.from("subvenciones").select("titulo, fecha_inicio, fecha_fin").in("codigo", codes).limit(10);
        const list = (gs ?? []).filter((g: any) => !g.fecha_fin || g.fecha_fin >= today).map((g: any) => `«${String(g.titulo).slice(0, 90)}» (abre ${g.fecha_inicio ?? "?"}, cierra ${g.fecha_fin ?? "?"})`);
        if (list.length) grantLine = `Subvenciones que le interesan: ${list.join("; ")}.`;
      }
    } catch { /* sin subvenciones */ }
    const kind = businessKindFor(sector) === "hospitality" ? "hostelería (bar o restaurante)" : "comercio de calle (tienda o servicio)";
    const startedAt = Date.now();
    try {
      const lovable = createOpenAI({ baseURL: "https://ai.gateway.lovable.dev/v1", apiKey, headers: { "Lovable-API-Key": apiKey, "X-Lovable-AIG-SDK": "vercel-ai-sdk" } });
      const result = streamText({
        model: lovable.responses(MODEL),
        providerOptions: { openai: { forceReasoning: true, reasoningEffort: "low", reasoningSummary: "auto", store: false, include: ["reasoning.encrypted_content"] } },
        output: Output.object({ schema: OutSchema }),
        system: `Eres ${assistantName}, la asistente del Agente Retail IA de Retail Lemon que este empresario ha elegido para trabajar con él. Conoces a fondo el calendario comercial de España para comercio de calle y hostelería.
Propón hasta 6 fechas del MES REVISADO (o las dos semanas previas, si hay que preparar algo) que muevan la venta o la operativa de ESTE negocio concreto:
- fiestas mayores o patronales del municipio, ferias, mercados, eventos de la ciudad y festivos locales;
- puentes y festivos: di si compensa abrir o no según el tipo de zona (turística, centro comercial urbano o barrio residencial que se vacía);
- momentos fuertes de venta propios del sector (cambio de temporada, fechas de regalo, vuelta de vacaciones) y cuándo preparar compras, escaparate o equipo.
NO repitas fechas generales que ya tiene (Navidad, Reyes, rebajas, Black Friday, San Valentín, Día del Padre o de la Madre, festivos nacionales).
Usa el vocabulario de su sector: en una tienda habla de escaparate, colección, stock y probadores; nunca de carta, menú o platos. Solo en bares y restaurantes habla de carta.
Solo fechas que conozcas con seguridad; si no estás seguro de la fecha exacta de ese año, no la incluyas.
category: sales (venta), offer (escaparate o cambio de oferta), purchases (proveedores y plazos), legal (festivo, apertura o equipo).
Cada razón: una o dos frases directas sobre qué preparar. Sin cifras inventadas ni jerga de consultora. Sin emojis.
${colorRule}
Si tiene subvenciones que le interesan y su cierre cae en el mes revisado o el siguiente, puedes proponer UNA fecha (category purchases) unas dos semanas antes del cierre para reunir presupuestos y papeles. No repitas la apertura ni el cierre: ya están en su calendario.
Textos en español de España (es) y en inglés natural (en).`,
        prompt: `Hoy: ${today}. Mes revisado: ${month}. Tipo: ${kind}. Sector: ${sector || "sin indicar"}. Comunidad: ${region || "sin indicar"}. Ciudad: ${city || "sin indicar"}. ${grantLine}`,
      });
      const output = await result.output;
      const usage = await Promise.resolve(result.usage).catch(() => null);
      await logAiUsage({ userId: context.userId, featureType: "calendar_radar", model: MODEL, usage: usage ? { prompt_tokens: usage.inputTokens ?? 0, completion_tokens: usage.outputTokens ?? 0, total_tokens: usage.totalTokens ?? 0 } : null, latencyMs: Date.now() - startedAt });

      const rows = output.events
        .filter((e) => e.date_start >= today && (!e.date_end || e.date_end >= e.date_start))
        .slice(0, 6)
        .map((e) => ({
          user_id: context.userId,
          date_start: e.date_start,
          date_end: e.date_end,
          category: e.category,
          title: (data.lang === "es" ? e.title_es : e.title_en).slice(0, 120) || e.title_es.slice(0, 120),
          reason: (data.lang === "es" ? e.reason_es : e.reason_en).slice(0, 600),
          source: "ai_suggestion",
          status: "suggested",
          lead_days: 21,
          ai_context: ctx,
          color: e.color && labelKeys.includes(e.color) && (CAL_COLORS as readonly string[]).includes(e.color) ? e.color : null,
        }));
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      if (rows.length) await supabaseAdmin.from("user_calendar_events").insert(rows as any);
      else await supabaseAdmin.from("user_calendar_events").insert({ user_id: context.userId, date_start: today, category: "sales", title: "-", source: "ai_suggestion", status: "inactive", lead_days: 0, ai_context: ctx } as any);
      return { ok: true as const, added: rows.length, cached: false, sector, region, city };
    } catch (e) {
      console.error("[calendar-radar]", String(e instanceof Error ? e.message : e).slice(0, 300));
      await logAiUsage({ userId: context.userId, featureType: "calendar_radar", model: MODEL, usage: null, latencyMs: Date.now() - startedAt, failed: true });
      return { ok: false as const, error: "ai_error" };
    }
  });
